import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { cn, initials } from '@/lib/utils';

/* -------------------------------------------------------------------------- */
/* Avatar                                                                     */
/* -------------------------------------------------------------------------- */

export function Avatar({
  name,
  src,
  size = 'md',
  className,
}: {
  name: string;
  src?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}) {
  const sizes = {
    sm: 'size-8 text-[11px]',
    md: 'size-10 text-xs',
    lg: 'size-12 text-sm',
    xl: 'size-16 text-lg',
  };

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={cn('shrink-0 rounded-full object-cover', sizes[size], className)}
      />
    );
  }

  return (
    <span
      aria-hidden
      title={name}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full bg-brand-100 font-bold text-brand-700',
        sizes[size],
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Segmented control (chart range switches, mobile history filters)           */
/* -------------------------------------------------------------------------- */

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  size = 'md',
  tone = 'brand',
  className,
}: {
  value: T;
  options: Array<SegmentedOption<T>>;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  tone?: 'brand' | 'receive' | 'dispatch';
  className?: string;
}) {
  const activeTone = {
    brand: 'bg-brand-600 text-white',
    receive: 'bg-receive-600 text-white',
    dispatch: 'bg-dispatch-600 text-white',
  }[tone];

  return (
    <div
      role="tablist"
      className={cn(
        // `max-w-full` + horizontal scroll rather than `inline-flex` alone: with
        // four report types this control is ~390px, which is wider than a phone,
        // and an inline-flex that cannot shrink pushes the whole page sideways.
        // Now it scrolls within itself instead.
        'flex max-w-full items-center gap-1 overflow-x-auto rounded-xl bg-canvas p-1',
        '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'shrink-0 rounded-lg font-semibold whitespace-nowrap transition-colors',
              size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-3.5 py-2 text-[13px]',
              active ? activeTone : 'text-ink-soft hover:bg-white hover:text-ink',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Page header + breadcrumbs                                                  */
/* -------------------------------------------------------------------------- */

export interface Crumb {
  label: string;
  to?: string;
}

/**
 * Breadcrumbs on the left, page actions on the right.
 *
 * Deliberately has **no heading** — the topbar renders the page title from the
 * route handle, and repeating it here produced two H1s stacked on top of each
 * other. Pass `note` for a short line of context instead.
 */
export function PageToolbar({
  crumbs,
  note,
  actions,
  className,
}: {
  crumbs?: Crumb[];
  note?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  const hasLeft = Boolean((crumbs && crumbs.length > 0) || note);
  if (!hasLeft && !actions) return null;

  return (
    <div
      className={cn(
        'mb-4 flex flex-wrap items-center gap-3',
        hasLeft ? 'justify-between' : 'justify-end',
        className,
      )}
    >
      {hasLeft && (
        <div className="min-w-0">
          {crumbs && crumbs.length > 0 && (
            <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-[13px]">
              {crumbs.map((crumb, index) => (
                <span key={`${crumb.label}-${index}`} className="flex items-center gap-1">
                  {index > 0 && <ChevronRight className="size-3.5 text-ink-muted" aria-hidden />}
                  {crumb.to ? (
                    <Link to={crumb.to} className="font-medium text-brand-600 hover:underline">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-ink-muted">{crumb.label}</span>
                  )}
                </span>
              ))}
            </nav>
          )}
          {note && <p className={cn(crumbs?.length && 'mt-1', 'text-[13px] text-ink-muted')}>{note}</p>}
        </div>
      )}
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Key/value rows (carton details, receipts, confirmations)                    */
/* -------------------------------------------------------------------------- */

export function DetailRow({
  label,
  value,
  valueClassName,
  className,
}: {
  label: string;
  value: ReactNode;
  valueClassName?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center justify-between gap-4 py-2.5', className)}>
      <dt className="text-[13px] text-ink-muted">{label}</dt>
      <dd className={cn('text-right text-[13px] font-semibold text-ink', valueClassName)}>{value}</dd>
    </div>
  );
}

export function DetailList({ children, className }: { children: ReactNode; className?: string }) {
  return <dl className={cn('divide-y divide-line', className)}>{children}</dl>;
}

/** Monospace-ish code chip for QR codes / track ids. */
export function CodeChip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'rounded-md bg-canvas px-1.5 py-0.5 font-mono text-[12.5px] font-semibold tracking-tight text-ink',
        className,
      )}
    >
      {children}
    </span>
  );
}
